import type {
  ManagedScheduler as IManagedScheduler,
  SchedulerHandle,
} from '../contracts/scheduler';

type PendingEntry = {
  readonly handle: SchedulerHandle;
  readonly expectedAt: number;
  readonly timer: ReturnType<typeof setTimeout>;
};

/**
 * Timeout recursivo gerenciado (não setInterval solto).
 * SSR-safe: sem timers se setTimeout indisponível.
 */
export class ManagedScheduler implements IManagedScheduler {
  private nextId = 1;

  private readonly pending = new Map<number, PendingEntry>();

  private destroyed = false;

  private lastDrift: number | undefined;

  schedule(task: () => void, delayMs: number): SchedulerHandle {
    if (this.destroyed || typeof setTimeout === 'undefined') {
      return { id: -1 };
    }

    const id = this.nextId++;
    const handle: SchedulerHandle = { id };
    const safeDelay = Math.max(0, delayMs);
    const expectedAt = Date.now() + safeDelay;

    const timer = setTimeout(() => {
      this.pending.delete(id);
      this.lastDrift = Date.now() - expectedAt;
      if (this.destroyed) return;
      try {
        task();
      } catch {
        // Isolation: task throw não deixa handles órfãos.
      }
    }, safeDelay);

    this.pending.set(id, { handle, expectedAt, timer });
    return handle;
  }

  cancel(handle: SchedulerHandle): void {
    const entry = this.pending.get(handle.id);
    if (!entry) return;
    clearTimeout(entry.timer);
    this.pending.delete(handle.id);
  }

  cancelAll(): void {
    for (const entry of this.pending.values()) {
      clearTimeout(entry.timer);
    }
    this.pending.clear();
  }

  activeCount(): number {
    return this.pending.size;
  }

  lastDriftMs(): number | undefined {
    return this.lastDrift;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.cancelAll();
    this.destroyed = true;
  }

  /** Reativa após destroy (reinit do engine). */
  reset(): void {
    this.cancelAll();
    this.destroyed = false;
    this.lastDrift = undefined;
  }

  isDestroyed(): boolean {
    return this.destroyed;
  }
}
